import { twMerge } from 'tailwind-merge'
import { type CSSProperties } from 'react'
import {
  getUntitledIconComponent,
  isUntitledIconName,
  type UntitledIconName
} from './untitled-ui-icon-registry'

export const BADGE_SYMBOL_COLOR_TOKENS = {
  slate: '#344054',
  violet: '#6941C6',
  teal: '#0F766E',
  orange: '#C2410C',
  sky: '#1D4ED8',
  lime: '#4D7C0F',
  rose: '#BE123C',
  amber: '#B45309',
  pink: '#C11574'
} as const

export const BADGE_SYMBOL_BACKGROUND_TOKENS = {
  white: '#FFFFFF',
  neutral: '#FCFCFD',
  soft: '#F8FAFC'
} as const

export const BADGE_SYMBOL_BORDER_TOKENS = {
  neutral: '#D0D5DD',
  muted: '#E4E7EC',
  selected: '#0BA5EC'
} as const

export type BadgeSymbolColorToken = keyof typeof BADGE_SYMBOL_COLOR_TOKENS
export type BadgeSymbolBackgroundToken =
  keyof typeof BADGE_SYMBOL_BACKGROUND_TOKENS
export type BadgeSymbolBorderToken = keyof typeof BADGE_SYMBOL_BORDER_TOKENS

export interface BadgeSymbolIconProps {
  iconName: UntitledIconName | string
  size?: number
  colorToken?: BadgeSymbolColorToken
  backgroundToken?: BadgeSymbolBackgroundToken
  borderToken?: BadgeSymbolBorderToken
  selected?: boolean
  className?: string
}

const DEFAULT_ICON_SIZE = 56

export function BadgeSymbolIcon({
  iconName,
  size = DEFAULT_ICON_SIZE,
  colorToken = 'slate',
  backgroundToken = 'white',
  borderToken = 'neutral',
  selected = false,
  className
}: BadgeSymbolIconProps) {
  const Icon = getUntitledIconComponent(iconName)
  const iconSize = Math.round(size * 0.42)
  const outlineSize = Math.round(iconSize * 1.12)
  const iconColor = BADGE_SYMBOL_COLOR_TOKENS[colorToken]
  const backgroundColor = BADGE_SYMBOL_BACKGROUND_TOKENS[backgroundToken]
  const borderColor = selected
    ? BADGE_SYMBOL_BORDER_TOKENS.selected
    : BADGE_SYMBOL_BORDER_TOKENS[borderToken]
  const outlineStyle: CSSProperties = {
    color: '#FFFFFF',
    height: outlineSize,
    left: '50%',
    position: 'absolute',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: outlineSize
  }
  const foregroundStyle: CSSProperties = {
    color: iconColor,
    height: iconSize,
    left: '50%',
    position: 'absolute',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: iconSize
  }

  return (
    <div
      aria-label={
        isUntitledIconName(iconName) ? iconName : `${iconName}-fallback`
      }
      className={twMerge(
        'relative inline-flex items-center justify-center rounded-[10px] border shadow-[0_1px_2px_rgba(16,24,40,0.06)]',
        className
      )}
      style={{
        backgroundColor,
        borderColor,
        boxShadow: selected
          ? '0 0 0 2px rgba(11, 165, 236, 0.12), 0 1px 2px rgba(16, 24, 40, 0.06)'
          : '0 1px 2px rgba(16, 24, 40, 0.06)',
        height: size,
        width: size
      }}>
      <Icon aria-hidden style={outlineStyle} />
      <Icon aria-hidden style={foregroundStyle} />
    </div>
  )
}
